import { profiles, normalizeHost } from '../core/profiles.js';
import { account } from '../core/account.js';
import { net } from '../core/net.js';
import { parseM3U } from '../core/m3u.js';
import { device } from '../core/device.js';
import { settings } from '../core/storage.js';
import { t } from '../core/i18n.js';
import { addPasswordToggles } from '../core/pwtoggle.js';
import { escapeHtml } from '../core/utils.js';
import { notify } from '../core/notify.js';
import qrcode from 'qrcode-generator';
import { subscriptionOf } from '../core/subscription.js';
import { makeClient, PROVIDER_TYPES, providerType } from '../core/clients.js';
import { SOURCE_GROUPS } from '../core/storage-sources.js';
import { hero as wizHero } from './add-source.js';
import { storage } from '../core/storage.js';
import { PRESETS, COUNTRY_LISTS, presetById, regionOf } from '../core/presets.js';
import { contentBlock } from '../core/content-block.js';
import { countryName } from '../core/country.js';
import { i18n } from '../core/i18n.js';

/* Pantalla inicial. Con cuentas activadas en el servidor: entrar con Google (o vincular una TV
   con un código). Después, o si las cuentas están desactivadas: alta/edición de proveedores
   (Xtream Codes o M3U). */

function isLocalDev() {
    return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
}

/** Enlace de descarga de la APK (solo web, cuando el servidor la tiene publicada). */
function apkLinkHtml() {
    if (device.isCordova || device.isElectron || !account.apkVersion) return '';
    return `<a class="external apk-link" href="${escapeHtml(account.baseUrl())}/apk" target="_blank" rel="noopener"><span class="mi">android</span><span>${t('app.downloadApk')}<small>${t('app.downloadApkSub', { url: escapeHtml(account.baseUrl().replace(/^https?:\/\//, '') + '/apk') })}</small></span><span class="mi">download</span></a>`;
}

function pairUrl() {
    return account.baseUrl().replace(/^https?:\/\//, '') + '/pair';
}

function pairQrSvg(code) {
    try {
        const qr = qrcode(0, 'M');
        qr.addData(account.baseUrl() + '/pair?code=' + code);
        qr.make();
        return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
    } catch (e) {
        return '';
    }
}

/** Entrada con correo y contraseña de la propia plataforma (solo si el servidor la tiene activada). */
function passwordBlockHtml() {
    return `<div class="pw-block" data-pw>
        <div class="segmented" data-pw-tabs>
            <button type="button" class="is-active" data-pw-tab="login">${t('login.pw.tabLogin')}</button>
            <button type="button" data-pw-tab="register">${t('login.pw.tabRegister')}</button>
        </div>
        <form class="pw-form" data-pw-form novalidate>
            <div class="field" data-pw-name hidden><label>${t('login.pw.name')}</label><input name="name" autocomplete="name" maxlength="80"/></div>
            <div class="field"><label>${t('login.pw.email')}</label><input name="email" type="email" inputmode="email" autocomplete="email" autocapitalize="off" spellcheck="false" maxlength="254"/></div>
            <div class="field"><label>${t('login.pw.password')}</label><input name="password" type="password" autocomplete="current-password" maxlength="128"/><small class="field-help" data-pw-help hidden>${t('login.pw.passwordHelp')}</small></div>
            <button type="button" class="link-btn" data-pw-forgot>${t('login.pw.forgot')}</button>
            <div class="field-error" data-pw-error hidden></div>
            <button type="submit" class="btn btn-accent btn-lg btn-block" data-pw-submit>${t('login.pw.submitLogin')}</button>
        </form>
        <form class="pw-form" data-pw-forgot-form novalidate hidden>
            <p class="pw-note">${t('login.pw.forgotIntro')}</p>
            <div class="field"><label>${t('login.pw.email')}</label><input name="email" type="email" inputmode="email" autocomplete="email" autocapitalize="off" spellcheck="false" maxlength="254"/></div>
            <div class="field-error" data-pw-error hidden></div>
            <button type="submit" class="btn btn-accent btn-lg btn-block">${t('login.pw.sendCode')}</button>
            <button type="button" class="link-btn" data-pw-back>${t('login.pw.back')}</button>
        </form>
        <form class="pw-form" data-pw-code-form novalidate hidden>
            <p class="pw-note" data-pw-code-note></p>
            <div class="field"><label>${t('login.pw.code')}</label><input name="code" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="one-time-code" class="pw-code"/></div>
            <div class="field" data-pw-newpass hidden><label>${t('login.pw.newPassword')}</label><input name="password" type="password" autocomplete="new-password" maxlength="128"/><small class="field-help">${t('login.pw.passwordHelp')}</small></div>
            <div class="field-error" data-pw-error hidden></div>
            <button type="submit" class="btn btn-accent btn-lg btn-block" data-pw-code-submit>${t('login.pw.verify')}</button>
            <button type="button" class="btn btn-block" data-pw-resend>${t('login.pw.resend')}</button>
            <button type="button" class="link-btn" data-pw-back>${t('login.pw.back')}</button>
        </form>
        <div class="auth-or"><span>${t('login.pw.or')}</span></div>
    </div>`;
}

function googleCardHtml() {
    const showPair = device.isTV || !account.canUseGoogleButton();
    // En una tele, escribir correo y contraseña con el mando es incómodo: allí se vincula con un código.
    const showPassword = account.passwordAuth && !device.isTV;
    return `<div class="auth-card" data-google-card>
        <div class="auth-brand">
            <img src="img/logo.png" alt=""/>
            <div><h1>${t('login.googleTitle')}</h1><small>${t(showPassword ? 'login.pw.subtitle' : 'login.googleSubtitle')}</small></div>
        </div>
        <div class="invite-note" data-invite hidden></div>
        ${showPassword ? passwordBlockHtml() : ''}
        ${account.canUseGoogleButton() ? `<div class="google-btn" data-google-btn><div class="spinner" style="width:22px;height:22px;border-width:2px"></div><span>${t('login.googleLoading')}</span></div>` : ''}
        <div class="field-error" data-error hidden></div>
        <div class="pair-box" data-pair-box ${showPair ? '' : 'hidden'}>
            <h2>${t('login.pairTitle')}</h2>
            <p>${t('login.pairQrHint')}</p>
            <div class="pair-qr" data-pair-qr aria-hidden="true"></div>
            <p>${t('login.pairHint', { url: '<b>' + escapeHtml(pairUrl()) + '</b>' })}</p>
            <div class="pair-code" data-pair-code aria-live="polite">······</div>
            <div class="pair-status" data-pair-status><div class="spinner" style="width:16px;height:16px;border-width:2px"></div><span>${t('login.pairWaiting')}</span></div>
            <button type="button" class="btn btn-block" data-pair-new hidden data-nav-default><span class="mi">refresh</span>${t('login.pairNew')}</button>
        </div>
        ${!showPair ? `<button type="button" class="btn btn-ghost btn-block" data-pair-show><span class="mi">tv</span>${t('login.pairShow')}</button>` : ''}
        ${device.isCordova || device.isElectron ? `<button type="button" class="btn btn-ghost btn-block" data-server><span class="mi">dns</span>${t('login.serverUrl', { url: escapeHtml(account.baseUrl().replace(/^https?:\/\//, '')) })}</button>` : ''}
        ${apkLinkHtml()}
    </div>`;
}

/** Países con lista pública, por nombre en el idioma de la interfaz. */
function countryOptions() {
    return COUNTRY_LISTS
        .map(([code, fallback]) => ({ code, name: countryName(regionOf(code), i18n.lang) === regionOf(code) ? fallback : countryName(regionOf(code), i18n.lang) }))
        .sort((a, b) => a.name.localeCompare(b.name, i18n.lang));
}


/** Primera vez (sin ninguna fuente): no se ofrece la fila «Televisión (IPTV)» (Xtream y M3U); esas dos se añaden ya dentro, en Ajustes → Fuentes. Empieza en «Emisiones en abierto». */
const hideIptvAtStart = (editing) => !editing && profiles.list().length === 0;

/* Pantalla «Conecta tu contenido» (diseño 07-10, pensado para la tele): pestañas grandes por grupo, filas con flecha para las
   emisiones en abierto, país aparte, herramientas en una fila y el botón Conectar. Con el mando: pestañas → filas → país →
   herramientas → Conectar. */
const GROUP_ICON = { free: 'live_tv', library: 'video_library', files: 'folder_open', iptv: 'tv' };
const GROUP_SUB = { free: 'login.freeDesc', library: 'login.libraryHint', files: 'login.filesHint', iptv: 'login.iptvDesc' };
const PRESET_ORDER = ['tdt-tv', 'tdt-radio', 'archive'];

function typePickerHtml(p, editing) {
    const active = providerType(p.type).group;
    const base = editing ? ['iptv', 'library', 'files'] : hideIptvAtStart(editing) ? ['free', 'library', 'files'] : ['free', 'library', 'files', 'iptv']; // primera vez: sin la pestaña IPTV (se llega por «Más formas de añadir»)
    // Abierto desde el asistente con un tipo fijo (Xtream, M3U…) el selector va oculto, pero el grupo de ese tipo tiene que existir.
    const groups = base.indexOf(active) === -1 ? base.concat(active) : base;
    const tabs = `<div class="type-tabs" data-nav-row>${groups.map((g) => `<button type="button" class="type-tab ${g === active ? 'is-active' : ''}" data-group="${g}" aria-pressed="${g === active}"><span class="mi">${GROUP_ICON[g]}</span><span>${t('login.tab.' + g)}</span></button>`).join('')}</div>`;
    const subs = groups.map((g) => {
        const types = PROVIDER_TYPES.filter((x) => x.group === g);
        return `<div class="type-sub" data-type-sub="${g}" ${g === active && types.length > 1 ? '' : 'hidden'}><div class="segmented" data-nav-row>${types.map((x) => `<button type="button" class="${p.type === x.id ? 'is-active' : ''}" data-value="${x.id}">${t('login.type.' + x.id)}</button>`).join('')}</div></div>`;
    }).join('');
    const heads = groups.map((g) => `<div class="login-head" data-group-head="${g}" ${g === active ? '' : 'hidden'}><h2>${t('login.head.' + g)}</h2><p>${t(GROUP_SUB[g])}</p></div>`).join('');
    return tabs + subs + heads;
}

/** Filas de emisiones en abierto, en el orden del diseño; «Canales públicos de España» ya lo cubre el país. La primera va marcada. */
function presetListHtml(firstRun) {
    const list = PRESETS.filter((x) => x.id !== 'iptv-org-es' && (x.provider || !contentBlock.isListBlocked(x.id)))
        .sort((a, b) => (PRESET_ORDER.indexOf(a.id) + 1 || 99) - (PRESET_ORDER.indexOf(b.id) + 1 || 99));
    const row = (id, icon, title, sub, checked, extra = '') => `<label class="preset" data-preset="${id}" data-focusable tabindex="0" data-nav-row data-nav-reach ${extra}><input type="radio" name="preset" value="${id}" ${checked ? 'checked' : ''}/><span class="mi preset-icon">${icon}</span><span class="preset-body"><b>${title}</b><small>${sub}</small></span><span class="mi preset-chevron" aria-hidden="true">chevron_right</span></label>`;
    return `<div class="preset-list" data-nav-list>${list.map((x, i) => row(x.id, x.icon, escapeHtml(t('preset.' + x.id)), escapeHtml(t('preset.' + x.id + '.sub')), i === 0, i === 0 && firstRun ? 'data-nav-default' : '')).join('')}${row('country', 'public', escapeHtml(t('preset.country')), escapeHtml(t('preset.country.sub', { n: COUNTRY_LISTS.length })), false)}</div>`;
}

/** Bandera del país como emoji regional (en Windows se ven las dos letras). */
function flagOf(region) {
    const r = String(region || '').toUpperCase();
    return /^[A-Z]{2}$/.test(r) ? String.fromCodePoint(...Array.from(r).map((c) => 127397 + c.charCodeAt(0))) : '';
}

/** «País» para «Canales internacionales»: un desplegable con bandera, debajo de la lista. */
function countryFieldHtml() {
    const options = countryOptions().filter((c) => !contentBlock.isListBlocked('iptv-org-' + c.code)).map((c) => `<option value="${c.code}">${flagOf(regionOf(c.code))} ${escapeHtml(c.name)}</option>`).join('');
    return `<div class="field country-field" data-country-field data-nav-row data-nav-reach><label>${t('login.country')}</label><div class="country-select"><select name="country" aria-label="${escapeHtml(t('login.country'))}">${options}</select><span class="mi">expand_more</span></div></div>`;
}

/** Fila de herramientas: asistente, buscar en la red, emparejar con la tele… (solo las que tengan sentido en este aparato). */
function toolsHtml(editing, locked) {
    const out = [];
    if (hideIptvAtStart(editing) && !locked && window.IPTV && window.IPTV.openAddSource) out.push(`<button type="button" class="btn login-tool" data-wizard><span class="mi">add_circle</span><span>${t('addsrc.moreWays')}</span></button>`);
    if (!out.length) return '';
    // Plegado, como en el diseño: una fila «Más opciones» que despliega las herramientas.
    return `<div class="login-more" data-more>
        <button type="button" class="login-more-toggle" data-more-toggle aria-expanded="false" data-nav-row data-nav-reach><span class="mi">add_circle_outline</span><span class="login-more-text">${t('login.moreOptions')}</span><span class="mi login-more-chev">expand_more</span></button>
        <div class="login-tools" data-more-panel hidden data-nav-row data-nav-reach>${out.join('')}</div>
    </div>`;
}

function providerFormHtml(p, editing, locked = false) {
    const hasProfiles = profiles.list().length > 0;
    const signed = account.signedIn;
    const title = locked ? t('addsrc.type.' + p.type) : editing ? t('settings.editProfile') : signed ? t('login.hello', { name: escapeHtml((account.user.name || account.user.email).split(' ')[0]) }) : t('login.title');
    // Con un tipo fijo (asistente «Añadir fuente») el subtítulo habla de ese grupo, no del genérico de la primera pantalla.
    const subtitle = locked ? t(GROUP_SUB[providerType(p.type).group] || 'login.subtitle') : editing ? t('login.subtitle') : signed ? t('login.addProviderSub') : t('login.subtitle');
    const brand = `<div class="auth-brand">
            ${signed && account.user.picture ? `<img class="auth-avatar" src="${escapeHtml(account.user.picture)}" alt="" referrerpolicy="no-referrer"/>` : '<img src="img/logo.png" alt=""/>'}
            <div><h1>${title}</h1><small>${subtitle}</small></div>
        </div>`;
    // Con tipo fijo (desde el asistente): la cabecera compacta del asistente (flecha + marca + título) y su dibujo del grupo.
    const wizGroup = locked ? (SOURCE_GROUPS.find((g) => g.types.indexOf(p.type) !== -1) || {}).id : '';
    const head = locked ? `<div class="wiz-head">${brand}</div>${wizHero(wizGroup)}` : brand;
    return `<form class="auth-card form${!editing && !locked ? ' is-hero' : ''}" data-form novalidate>
        ${head}
        <div class="type-picker" data-type ${locked ? 'hidden' : ''}>
            ${typePickerHtml(p, editing)}
        </div>
        <div data-free hidden>
            ${presetListHtml(hideIptvAtStart(editing))}
            ${countryFieldHtml()}
        </div>
        <div class="field" data-name-field data-nav-row data-nav-reach><label>${t('login.name')}</label><input name="name" value="${escapeHtml(p.name)}" placeholder="${t('login.namePh')}" autocomplete="off" data-nav-default/></div>
        <div data-xtream ${p.type === 'm3u' ? 'hidden' : ''}>
            <div class="field" data-nav-row data-nav-reach><label>${t('login.host')}</label><input name="host" value="${escapeHtml(p.host)}" placeholder="${t('login.hostPh.' + (p.type === 'm3u' ? 'xtream' : p.type))}" inputmode="url" autocomplete="off" autocapitalize="off"/></div>
            <div class="field mt-2" data-nav-row data-nav-reach data-user-field ${p.type === 'plex' ? 'hidden' : ''}><label>${t('login.user')}</label><input name="username" value="${escapeHtml(p.type === 'plex' ? '' : p.username)}" autocomplete="username" autocapitalize="off"/></div>
            <div class="field mt-2" data-nav-row data-nav-reach><label data-pass-label>${t(p.type === 'plex' ? 'login.plexToken' : 'login.pass')}</label><input name="password" type="password" value="${escapeHtml(p.password)}" autocomplete="current-password"/><small class="field-help" data-token-help ${p.type === 'plex' ? '' : 'hidden'}>${t('login.plexTokenHelp')}</small></div>
        </div>
        <div data-m3u ${p.type === 'm3u' ? '' : 'hidden'}>
            <div class="field" data-nav-row data-nav-reach><label>${t('login.m3u')}</label><input name="m3uUrl" value="${escapeHtml(p.m3uUrl)}" placeholder="${t('login.m3uPh')}" inputmode="url" autocomplete="off" autocapitalize="off"/></div>
            <div class="field mt-2" data-nav-row data-nav-reach><label>${t('login.epg')}</label><input name="epgUrl" value="${escapeHtml(p.epgUrl)}" inputmode="url" autocomplete="off" autocapitalize="off"/></div>
        </div>
        ${toolsHtml(editing, locked)}
        <p class="login-note" data-free-note hidden><span class="mi">info</span><span>${t('login.freeHint')}</span></p>
        <div class="field-error" data-error hidden></div>
        <button type="submit" class="btn btn-accent btn-lg btn-block" data-submit><span class="mi">login</span>${editing ? t('common.save') : t('login.connect')}</button>
        ${hasProfiles || locked ? `<button type="button" class="btn btn-block" data-cancel>${t(locked ? 'addsrc.back' : 'common.cancel')}</button>` : ''}
        ${!editing && isLocalDev() ? `<button type="button" class="btn btn-ghost btn-block" data-demo><span class="mi">science</span>${t('login.demo')}</button>` : ''}
        ${signed && !hasProfiles ? `<button type="button" class="btn btn-ghost btn-block" data-signout><span class="mi">logout</span>${t('login.signOut')}</button>` : ''}
        ${apkLinkHtml()}
    </form>`;
}

export default {
    path: '/login/',
    alias: ['/profile/:id/'],
    nav: null,

    render(route) {
        const found = route.params && route.params.id ? profiles.get(route.params.id) : null;
        const editing = found && !found.shared ? found : null; // un proveedor compartido no se edita ni se enseñan sus datos
        // Desde el asistente «Añadir fuente» llega con el tipo ya elegido (?type=…&wizard=1) y sin el selector de tipos.
        const q = (route && route.query) || {};
        const locked = !editing && !!q.wizard && !!PROVIDER_TYPES.find((x) => x.id === q.type);
        const p = editing || { type: locked ? q.type : hideIptvAtStart(editing) ? 'free' : 'xtream', name: locked ? String(q.name || '') : '', host: locked ? String(q.host || '') : '', username: '', password: '', m3uUrl: '', epgUrl: '' };
        const needsGoogle = account.enabled && !account.signedIn;
        const hero = !needsGoogle && !editing && !locked;
        return `<div class="page no-nav" data-name="login">
            <div class="page-content" style="padding:0">
                <div class="auth${hero ? ' auth-hero' : ''}${locked ? ' wiz-page wiz-form-page' : ''}">${hero ? '<div class="auth-top"><img src="img/logo.png" alt=""/><span>Filoteca</span></div>' : ''}${needsGoogle ? googleCardHtml() : providerFormHtml(p, editing, locked)}</div>
            </div>
        </div>`;
    },

    mount(page, route) {
        const root = page.el;
        if (account.enabled && !account.signedIn) return mountGoogle(root);
        const shared = route.params && route.params.id ? profiles.get(route.params.id) : null;
        if (shared && shared.shared) { window.app.views.main.router.navigate('/settings/'); return; }
        // Sin ninguna fuente y sin venir de «Añadir fuente», la pantalla de siempre es la de inicio (asistente), no este formulario.
        const q = (route && route.query) || {};
        if (!(route.params && route.params.id) && !q.wizard && !profiles.list().length) { window.app.views.main.router.navigate('/start/', { reloadAll: true, clearPreviousHistory: true }); return; }
        return mountForm(root, route);
    }
};

/** Entrada con correo y contraseña: entrar, crear cuenta (con código por correo) y recuperar la contraseña. */
function mountPassword(root, finish) {
    const box = root.querySelector('[data-pw]');
    if (!box) return;
    const loginForm = box.querySelector('[data-pw-form]');
    const forgotForm = box.querySelector('[data-pw-forgot-form]');
    const codeForm = box.querySelector('[data-pw-code-form]');
    const tabs = box.querySelector('[data-pw-tabs]');
    const state = { mode: 'login', email: '', kind: '', busy: false, timer: null };

    const errorOf = (form) => form.querySelector('[data-pw-error]');
    const showError = (form, msg) => {
        const el = errorOf(form);
        el.textContent = msg;
        el.hidden = !msg;
    };
    const messageFor = (err) => {
        const code = err && err.code;
        const key = 'login.pw.error.' + code;
        if (code === 'twofa_cancelled') return ''; // cerró el cuadro del código: no es un error
        if (code === 'not_allowed') return t('login.pw.notAllowed');
        if (code === 'registration_full') return t('login.registrationFull');
        const text = code ? t(key) : '';
        return text && text !== key ? text : (err && err.status ? t('login.pw.error.generic') : t('common.errorNetwork'));
    };
    const busy = (form, on) => {
        state.busy = on;
        form.querySelectorAll('button[type="submit"]').forEach((b) => { b.disabled = on; });
    };
    const show = (which) => {
        loginForm.hidden = which !== 'login';
        forgotForm.hidden = which !== 'forgot';
        codeForm.hidden = which !== 'code';
        tabs.hidden = which !== 'login';
        [loginForm, forgotForm, codeForm].forEach((f) => showError(f, ''));
        const first = (which === 'login' ? loginForm : which === 'forgot' ? forgotForm : codeForm).querySelector('input:not([hidden])');
        if (first && !device.hasTouch) first.focus();
    };
    const setMode = (mode) => {
        state.mode = mode;
        tabs.querySelectorAll('[data-pw-tab]').forEach((b) => b.classList.toggle('is-active', b.getAttribute('data-pw-tab') === mode));
        const register = mode === 'register';
        loginForm.querySelector('[data-pw-name]').hidden = !register;
        loginForm.querySelector('[data-pw-help]').hidden = !register;
        loginForm.querySelector('[data-pw-forgot]').hidden = register;
        loginForm.password.setAttribute('autocomplete', register ? 'new-password' : 'current-password');
        loginForm.querySelector('[data-pw-submit]').textContent = t(register ? 'login.pw.submitRegister' : 'login.pw.submitLogin');
        showError(loginForm, '');
    };
    /** Cuenta atrás del botón de reenviar: el servidor no envía otro código hasta pasado un minuto. */
    const cooldown = () => {
        const btn = codeForm.querySelector('[data-pw-resend]');
        let left = 60;
        clearInterval(state.timer);
        const tick = () => {
            btn.disabled = left > 0;
            btn.textContent = left > 0 ? t('login.pw.resendIn', { n: left }) : t('login.pw.resend');
            if (left-- <= 0) clearInterval(state.timer);
        };
        tick();
        state.timer = setInterval(tick, 1000);
    };
    const toCodeStep = (kind, email, minutes) => {
        state.kind = kind;
        state.email = email;
        codeForm.querySelector('[data-pw-code-note]').textContent = t('login.pw.codeSent', { email, min: minutes || 15 });
        codeForm.querySelector('[data-pw-newpass]').hidden = kind !== 'reset';
        codeForm.querySelector('[data-pw-code-submit]').textContent = t(kind === 'reset' ? 'login.pw.reset' : 'login.pw.verify');
        codeForm.code.value = '';
        codeForm.password.value = '';
        show('code');
        cooldown();
    };

    tabs.addEventListener('click', (e) => {
        const b = e.target.closest('[data-pw-tab]');
        if (b) setMode(b.getAttribute('data-pw-tab'));
    });
    box.querySelector('[data-pw-forgot]').addEventListener('click', () => {
        forgotForm.email.value = loginForm.email.value;
        show('forgot');
    });
    box.querySelectorAll('[data-pw-back]').forEach((b) => b.addEventListener('click', () => { clearInterval(state.timer); show('login'); }));

    loginForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (state.busy) return;
        const email = loginForm.email.value.trim();
        const password = loginForm.password.value;
        if (!email || !password) return showError(loginForm, t('login.pw.error.fields'));
        showError(loginForm, '');
        busy(loginForm, true);
        try {
            if (state.mode === 'register') {
                const r = await account.register({ email, password, name: loginForm.name.value.trim() });
                toCodeStep('verify', r.email || email, r.expiresInMinutes);
            } else {
                await account.loginWithPassword({ email, password });
                finish();
            }
        } catch (err) {
            showError(loginForm, messageFor(err));
        } finally {
            busy(loginForm, false);
        }
    });

    forgotForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (state.busy) return;
        const email = forgotForm.email.value.trim();
        if (!email) return showError(forgotForm, t('login.pw.error.invalid_email'));
        showError(forgotForm, '');
        busy(forgotForm, true);
        try {
            const r = await account.forgotPassword({ email });
            toCodeStep('reset', r.email || email, r.expiresInMinutes);
        } catch (err) {
            showError(forgotForm, messageFor(err));
        } finally {
            busy(forgotForm, false);
        }
    });

    codeForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (state.busy) return;
        const code = codeForm.code.value.replace(/\D/g, '');
        if (code.length !== 6) return showError(codeForm, t('login.pw.error.code_invalid'));
        showError(codeForm, '');
        busy(codeForm, true);
        try {
            if (state.kind === 'reset') await account.resetPassword({ email: state.email, code, password: codeForm.password.value });
            else await account.verifyEmail({ email: state.email, code });
            clearInterval(state.timer);
            finish();
        } catch (err) {
            showError(codeForm, messageFor(err));
        } finally {
            busy(codeForm, false);
        }
    });

    codeForm.querySelector('[data-pw-resend]').addEventListener('click', async () => {
        if (state.busy) return;
        showError(codeForm, '');
        try {
            if (state.kind === 'reset') await account.forgotPassword({ email: state.email });
            else await account.register({ email: state.email, password: loginForm.password.value, name: loginForm.name.value.trim() });
            notify.toast(t('login.pw.resent'), { icon: 'mark_email_read' });
            cooldown();
        } catch (err) {
            showError(codeForm, messageFor(err));
        }
    });
}

/** Si se llegó con un enlace de invitación, lo comprueba y lo dice; una invitación válida abre «Crear cuenta». */
async function mountInvite(root) {
    const box = root.querySelector('[data-invite]');
    if (!box || !account.invite) return;
    const r = await account.checkInvite();
    if (!r || !root.contains(box)) return;
    box.hidden = false;
    box.innerHTML = '<span class="mi" aria-hidden="true"></span><span data-invite-text></span>';
    const say = (icon, text) => {
        box.querySelector('.mi').textContent = icon;
        box.querySelector('[data-invite-text]').textContent = text;
    };
    if (r.valid) {
        say('mark_email_read', t('login.invite.ok'));
        const tab = root.querySelector('[data-pw-tab="register"]');
        if (tab) tab.click();
    } else {
        box.classList.add('is-bad');
        say('link_off', t('login.invite.bad'));
        account.clearInvite();
    }
}

function mountGoogle(root) {
    const errorEl = root.querySelector('[data-error]');
    const showError = (msg) => {
        errorEl.textContent = msg;
        errorEl.hidden = !msg;
    };
    const finish = () => {
        notify.toast(t('login.success'), { icon: 'check_circle' });
        const current = profiles.current();
        if (/^\/pair\/?$/.test(location.pathname)) {
            window.app.views.main.router.navigate('/pair/', { reloadAll: true, clearPreviousHistory: true });
        } else if (current) {
            window.IPTV.actions.refreshViewer();
            window.IPTV.actions.activateProfile(current);
            // Con varios espectadores en la cuenta se pregunta quién ve, como al abrir la app.
            const next = window.IPTV.viewers.list().length > 1 ? '/who/' : '/';
            window.app.views.main.router.navigate(next, { reloadAll: true, clearPreviousHistory: true });
        } else if (/^\/login\/?$/.test(location.pathname)) {
            // Navegar a la misma ruta no repinta la pantalla: se recarga para que enseñe el formulario de proveedor.
            location.reload();
        } else {
            window.app.views.main.router.navigate('/login/', { reloadAll: true, clearPreviousHistory: true, ignoreCache: true });
        }
    };

    addPasswordToggles(root);
    mountPassword(root, finish);
    mountInvite(root);

    const btn = root.querySelector('[data-google-btn]');
    const pairBox = root.querySelector('[data-pair-box]');
    const showPair = root.querySelector('[data-pair-show]');
    if (btn) {
        const placeholder = Array.from(btn.children);
        account.renderGoogleButton(btn, (err) => {
            if (!err) return finish();
            showError(err.code === 'not_allowed' ? t('login.notAllowed') : err.code === 'registration_full' ? t('login.registrationFull') : t('login.googleError'));
        }).then(() => {
            placeholder.forEach((el) => el.remove());
        }).catch(() => {
            btn.hidden = true;
            showError(t('login.googleError'));
            pairBox.hidden = false;
            if (showPair) showPair.hidden = true;
            startPair();
        });
    }

    /* Emparejamiento por código */
    const codeEl = root.querySelector('[data-pair-code]');
    const qrEl = root.querySelector('[data-pair-qr]');
    const statusEl = root.querySelector('[data-pair-status]');
    const newBtn = root.querySelector('[data-pair-new]');
    let pairing = { cancelled: false, running: false };

    const startPair = async () => {
        if (pairing.running) return;
        pairing = { cancelled: false, running: true };
        const mine = pairing;
        newBtn.hidden = true;
        statusEl.hidden = false;
        codeEl.textContent = '······';
        qrEl.innerHTML = '';
        try {
            const { code, pollToken } = await account.startPairing();
            if (mine.cancelled) return;
            codeEl.textContent = code.split('').join(' ');
            qrEl.innerHTML = pairQrSvg(code);
            const session = await account.waitPairing(pollToken, { isCancelled: () => mine.cancelled });
            if (mine.cancelled) return;
            if (session) return finish();
            qrEl.innerHTML = '';
            statusEl.innerHTML = `<span class="mi">timer_off</span><span>${t('login.pairExpired')}</span>`;
        } catch (e) {
            if (mine.cancelled) return;
            statusEl.innerHTML = `<span class="mi">error_outline</span><span>${t('common.errorNetwork')}</span>`;
        } finally {
            mine.running = false;
        }
        newBtn.hidden = false;
    };
    newBtn.addEventListener('click', () => {
        statusEl.innerHTML = `<div class="spinner" style="width:16px;height:16px;border-width:2px"></div><span>${t('login.pairWaiting')}</span>`;
        startPair();
    });
    if (!pairBox.hidden) startPair();

    if (showPair) {
        showPair.addEventListener('click', () => {
            pairBox.hidden = false;
            showPair.hidden = true;
            startPair();
        });
    }

    const serverBtn = root.querySelector('[data-server]');
    if (serverBtn) {
        serverBtn.addEventListener('click', () => {
            window.app.dialog.prompt(t('settings.serverUrlSub'), t('settings.serverUrl'), (value) => {
                settings.set('serverUrl', (value || '').trim());
                location.reload();
            }, null, settings.get('serverUrl') || '');
        });
    }

    return () => {
        pairing.cancelled = true;
    };
}

function mountForm(root, route) {
    addPasswordToggles(root);
    const form = root.querySelector('[data-form]');
    const editingId = route.params && route.params.id;
    const state = { type: root.querySelector('[data-type] [data-value].is-active').getAttribute('data-value'), busy: false };
    const errorEl = root.querySelector('[data-error]');
    const submit = root.querySelector('[data-submit]');

    const showError = (msg) => {
        errorEl.textContent = msg;
        errorEl.hidden = !msg;
    };
    // Pestañas, fila de subtipos y cabecera de la sección: todo sigue al grupo del tipo elegido.
    const syncGroupUi = (g) => {
        root.querySelectorAll('[data-type] [data-group]').forEach((b) => { const on = b.getAttribute('data-group') === g; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
        root.querySelectorAll('[data-type-sub]').forEach((el) => { el.hidden = el.getAttribute('data-type-sub') !== g || el.querySelectorAll('[data-value]').length < 2; });
        root.querySelectorAll('[data-group-head]').forEach((el) => { el.hidden = el.getAttribute('data-group-head') !== g; });
    };

    root.querySelector('[data-type]').addEventListener('click', (e) => {
        // Pestaña de grupo: se pasa al tipo que estuviera elegido en ese grupo (o al primero).
        const tab = e.target.closest('[data-group]');
        if (tab) {
            const g = tab.getAttribute('data-group');
            const pick = root.querySelector('[data-type-sub="' + g + '"] [data-value].is-active') || root.querySelector('[data-type-sub="' + g + '"] [data-value]');
            if (pick && !pick.classList.contains('is-active')) pick.click();
            else if (pick) syncGroupUi(g);
            return;
        }
        const btn = e.target.closest('[data-value]');
        if (!btn) return;
        state.type = btn.getAttribute('data-value');
        root.querySelectorAll('[data-type] [data-value]').forEach((b) => b.classList.toggle('is-active', b === btn));
        const kind = providerType(state.type);
        syncGroupUi(kind.group);
        root.querySelector('[data-xtream]').hidden = kind.fields === 'm3u' || kind.fields === 'preset';
        root.querySelector('[data-m3u]').hidden = kind.fields !== 'm3u';
        root.querySelector('[data-free]').hidden = kind.fields !== 'preset';
        root.querySelector('[data-name-field]').hidden = kind.fields === 'preset';
        root.querySelector('[data-user-field]').hidden = kind.fields === 'token';
        root.querySelector('[data-pass-label]').textContent = t(kind.fields === 'token' ? 'login.plexToken' : 'login.pass');
        root.querySelector('[data-token-help]').hidden = kind.fields !== 'token';
        root.querySelector('[data-free-note]').hidden = kind.fields !== 'preset';
        if (kind.fields !== 'm3u' && kind.fields !== 'preset') form.host.placeholder = t('login.hostPh.' + state.type);
        showError('');
    });

    // Primera vez (o llegando desde el asistente): se deja el formulario como pide el tipo con el que empieza.
    if (state.type === 'free' || (route.query && route.query.wizard)) { const first = root.querySelector('[data-type] [data-value="' + state.type + '"]'); if (first) first.click(); }

    const moreToggle = root.querySelector('[data-more-toggle]');
    if (moreToggle) {
        moreToggle.addEventListener('click', () => {
            const panel = root.querySelector('[data-more-panel]');
            const open = panel.hidden;
            panel.hidden = !open;
            moreToggle.setAttribute('aria-expanded', String(open));
            root.querySelector('[data-more]').classList.toggle('is-open', open);
            if (open) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
    }

    // «Más formas de añadir…» abre el asistente «Añadir fuente».
    const wizardBtn = root.querySelector('[data-wizard]');
    if (wizardBtn) wizardBtn.addEventListener('click', () => window.IPTV.openAddSource());

    const countrySelect = root.querySelector('select[name="country"]');
    if (countrySelect) {
        const preferred = String(i18n.lang || 'es').slice(0, 2);
        const guess = { es: 'es', en: 'us', fr: 'fr', de: 'de', it: 'it', pt: 'pt' }[preferred];
        if (guess && Array.from(countrySelect.options).some((o) => o.value === guess)) countrySelect.value = guess;
        countrySelect.addEventListener('change', () => { const r = root.querySelector('input[name="preset"][value="country"]'); if (r) r.checked = true; });
    }

    // La flecha de la cabecera y el botón de abajo cancelan igual.
    root.querySelectorAll('[data-cancel]').forEach((cancel) => {
        cancel.addEventListener('click', () => {
            state.cancelled = true;
            // Desde la página de inicio (/start/) «atrás» ya devuelve al asistente: no hace falta abrirlo en ventana.
            const fromStart = window.app.views.main.router.history.some((u) => /^\/start\/?(\?|$)/.test(String(u)));
            window.IPTV.back();
            // Llegó desde el asistente «Añadir fuente» en ventana: se vuelve a su lista de tipos.
            if (route.query && route.query.wizard && !fromStart) {
                const g = SOURCE_GROUPS.find((x) => x.types.indexOf(state.type) !== -1);
                setTimeout(() => window.IPTV.openAddSource(g ? { group: g.id, step: 'type' } : {}), 300);
            }
        });
    });

    const signout = root.querySelector('[data-signout]');
    if (signout) signout.addEventListener('click', () => window.IPTV.actions.signOut());

    const demo = root.querySelector('[data-demo]');
    if (demo) {
        demo.addEventListener('click', () => {
            form.name.value = 'Demo';
            form.host.value = location.origin + '/mock';
            form.username.value = 'demo';
            form.password.value = 'demo';
            state.type = 'xtream';
            form.requestSubmit ? form.requestSubmit() : submit.click();
        });
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (state.busy) return;
        const data = {
            type: state.type,
            name: form.name.value.trim(),
            host: normalizeHost(form.host.value),
            username: form.username.value.trim(),
            password: form.password.value,
            m3uUrl: form.m3uUrl.value.trim(),
            epgUrl: form.epgUrl.value.trim()
        };
        // Canales gratuitos: la lista pública elegida se guarda como un proveedor M3U más.
        if (data.type === 'free') {
            const picked = (form.querySelector('[name="preset"]:checked') || {}).value;
            const preset = presetById(picked === 'country' ? 'iptv-org-' + form.country.value : picked);
            if (!preset) return;
            if (preset.provider === 'archive') {
                if (profiles.list().some((x) => x.type === 'archive')) {
                    showError(t('login.freeAlready'));
                    return;
                }
                Object.assign(data, { type: 'archive', name: t('preset.archive'), m3uUrl: '', epgUrl: '', host: '', username: '', password: '' });
            } else {
                if (profiles.list().some((x) => x.type === 'm3u' && x.m3uUrl === preset.m3uUrl)) {
                    showError(t('login.freeAlready'));
                    return;
                }
                Object.assign(data, { type: 'm3u', name: preset.country ? t('preset.countryName', { name: countryOptions().find((c) => c.code === preset.country).name }) : t('preset.' + preset.id), m3uUrl: preset.m3uUrl, epgUrl: preset.epgUrl, host: '', username: '', password: '' });
            }
        }
        // Si pegan una URL get.php de Xtream en el campo M3U, la convertimos en cuenta Xtream.
        if (data.type === 'm3u') {
            const parsed = profiles.fromM3uUrl(data.m3uUrl);
            if (parsed) Object.assign(data, parsed, { m3uUrl: '' });
        }
        const kind = providerType(data.type);
        // Plex entra con su token; el nombre de usuario queda fijo para que el servidor lo acepte.
        if (kind.fields === 'token') data.username = 'plex';
        const complete = kind.fields === 'none' ? true
            : kind.fields === 'm3u' ? !!data.m3uUrl
            : kind.fields === 'token' ? !!(data.host && data.password)
                : kind.fields === 'folder' ? !!data.host
                : kind.group === 'library' ? !!(data.host && data.username)
                    : !!(data.host && data.username && data.password);
        if (!complete) {
            showError(t('login.errorFields'));
            return;
        }
        state.cancelled = false;
        state.busy = true;
        submit.disabled = true;
        submit.innerHTML = `<div class="spinner" style="width:20px;height:20px;border-width:2px"></div>${t('login.connecting')}`;
        showError('');
        try {
            let sub = null;
            if (kind.fields !== 'm3u') {
                const client = makeClient(Object.assign({ id: editingId || 'nuevo' }, data));
                const auth = await client.auth();
                storage.remove('ms-session:nuevo');
                sub = subscriptionOf({ user: auth.user_info || {} });
            } else {
                const text = await net.fetchText(data.m3uUrl, { timeout: 60000 });
                const parsed = parseM3U(text);
                if (!parsed.live.length && !parsed.vod.length && !parsed.series.length) throw new Error('m3u');
            }
            if (state.cancelled) return; // pulsó «Atrás» mientras se comprobaba: no se da de alta ni se le saca de donde está
            const profile = editingId ? await profiles.update(editingId, data) : await profiles.add(data);
            // Una fuente nueva se suma a las que ya hay (la principal sigue siéndolo); solo la primera pasa a ser la principal.
            const joined = editingId ? (profiles.setCurrent(profile.id), false) : profiles.joinCatalog(profile.id);
            // Se puede conectar una cuenta caducada (para tenerla lista al renovar), pero se avisa.
            if (sub && sub.state === 'expired') notify.error(t('login.connectedExpired'));
            else notify.toast(t('login.success'), { icon: 'check_circle' });
            // La fuente que se suma a otra ya tiene su ficha guardada: su catálogo (puede ser enorme) se descarga en segundo plano, sin dejar el botón en «Conectando…».
            if (joined) window.IPTV.catalog.loadExtras({ force: true }).catch(() => {});
            else await window.IPTV.actions.activateProfile(profile, { force: !!editingId });
            window.app.views.main.router.navigate('/', { reloadAll: true, clearPreviousHistory: true });
        } catch (err) {
            console.warn('[login] error', err);
            if (kind.group === 'files') showError(t(err && err.code === 'auth' ? 'login.errorFilesAuth' : err && err.code === 'empty' ? 'login.errorFilesEmpty' : 'login.errorFiles'));
            else if (err && err.code === 'auth') showError(t(kind.fields === 'token' ? 'login.errorToken' : 'login.errorAuth'));
            else if (kind.group === 'library') showError(t('login.errorLibrary'));
            else if (err && err.message === 'm3u') showError(t('login.errorM3u'));
            else if (err && err.status === 401) showError(t('login.googleError'));
            else showError(t('login.errorHost') + (err && err.status ? ' (HTTP ' + err.status + ')' : ''));
        } finally {
            state.busy = false;
            submit.disabled = false;
            submit.innerHTML = `<span class="mi">login</span>${editingId ? t('common.save') : t('login.connect')}`;
        }
    });
    return () => {};
}
